import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Users, Calendar, ClipboardList, Clock, CheckCircle, AlertTriangle, Building2, Loader2, FileText, Bell, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BranchDashboard = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todaySummary, setTodaySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleRow = (key) => setExpandedRow((prev) => (prev === key ? null : key));

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/branch/stats`, { headers }),
        axios.get(`${API}/branch/attendance/today-summary`, { headers })
      ]);
      setStats(statsRes.data);
      setTodaySummary(summaryRes.data);
    } catch (error) {
      console.error('Dashboard data error:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  const statCards = [
    { 
      title: 'Bugün İşe Gelen', 
      value: todaySummary?.present || 0, 
      icon: CheckCircle, 
      color: 'text-green-600', 
      bgColor: 'bg-green-50' 
    },
    { 
      title: 'İzinli/Raporlu', 
      value: (todaySummary?.leave || 0) + (todaySummary?.sick || 0), 
      icon: Calendar, 
      color: 'text-orange-600', 
      bgColor: 'bg-orange-50' 
    },
    { 
      title: 'Aktif Görevler', 
      value: stats?.active_tasks || 0, 
      icon: ClipboardList, 
      color: 'text-purple-600', 
      bgColor: 'bg-purple-50' 
    },
  ];

  // Dikkat edilmesi gereken öğeleri hesapla
  const alerts = [];
  
  if (todaySummary?.not_recorded > 0) {
    alerts.push({
      type: 'warning',
      icon: Clock,
      title: 'Mesai Kaydı Eksik',
      description: `${todaySummary.not_recorded} çalışanın bugünkü mesai kaydı henüz girilmemiş`
    });
  }
  
  if (stats?.tasks_due_today > 0) {
    alerts.push({
      type: 'error',
      icon: ClipboardList,
      title: 'Bugün Son Gün!',
      description: `${stats.tasks_due_today} görevin bitiş tarihi bugün`
    });
  }
  
  if (stats?.calendar_notes_today > 0) {
    alerts.push({
      type: 'info',
      icon: Calendar,
      title: 'Bugünkü Takvim Notları',
      description: `${stats.calendar_notes_today} takvim notu bugün için planlanmış`
    });
  }
  
  if (stats?.pending_reports_today > 0) {
    alerts.push({
      type: 'error',
      icon: FileText,
      title: 'Rapor İsteği Son Gün!',
      description: `${stats.pending_reports_today} rapor isteğinin son günü bugün`
    });
  }

  const getAlertStyle = (type) => {
    switch (type) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  return (
    <div className="space-y-6" data-testid="branch-dashboard">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Building2 className="h-8 w-8" />
          <h1 className="text-2xl font-bold">Şube Yönetim Paneli</h1>
        </div>
        <p className="text-amber-100">
          Hoş geldin, {user?.name}! 
          <span className="font-semibold ml-2">
            {user?.branch_name ? `${user.branch_name} Şubesi` : 'Şubeniz'} 
          </span> operasyonlarını buradan yönetebilirsiniz.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Toplam Çalışan - Özel Kart */}
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500">Toplam Çalışan</p>
              <div className="p-3 rounded-lg bg-blue-50">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Fortis</span>
                <span className="font-semibold text-blue-600">{stats?.fortis_employees || 0}</span>
              </div>
              {stats?.subcontractor_details?.map((sub, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{sub.name}</span>
                  <span className="font-semibold text-orange-600">{sub.employee_count}</span>
                </div>
              ))}
              <div className="border-t pt-2 mt-2 flex justify-between items-center">
                <span className="font-medium text-gray-700">Toplam</span>
                <span className="text-xl font-bold text-gray-900">{stats?.total_employees || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Diğer istatistik kartları */}
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Attendance Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Clock className="h-5 w-5" />
              Bugünkü Mesai Özeti ({todaySummary?.date})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { key: 'present', label: 'İşe Gelenler', count: todaySummary?.present || 0, names: todaySummary?.present_names || [], box: 'bg-green-50', text: 'text-green-700', bold: 'text-green-800' },
                { key: 'absent', label: 'Gelmeyenler', count: todaySummary?.absent || 0, names: todaySummary?.absent_names || [], box: 'bg-red-50', text: 'text-red-700', bold: 'text-red-800' },
                { key: 'leave', label: 'İzinli', count: todaySummary?.leave || 0, names: todaySummary?.leave_names || [], box: 'bg-blue-50', text: 'text-blue-700', bold: 'text-blue-800' },
                { key: 'sick', label: 'Raporlu', count: todaySummary?.sick || 0, names: todaySummary?.sick_names || [], box: 'bg-purple-50', text: 'text-purple-700', bold: 'text-purple-800' },
              ].map((row) => {
                const hasNames = row.names && row.names.length > 0;
                const open = expandedRow === row.key;
                return (
                  <div key={row.key} className={`${row.box} rounded-lg`} data-testid={`summary-row-${row.key}`}>
                    <button
                      type="button"
                      onClick={() => hasNames && toggleRow(row.key)}
                      disabled={!hasNames}
                      className={`w-full flex items-center justify-between p-3 ${hasNames ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                      data-testid={`summary-toggle-${row.key}`}
                    >
                      <span className={`${row.text} font-medium flex items-center gap-1.5`}>
                        {row.label}
                        {hasNames && (
                          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                        )}
                      </span>
                      <span className={`${row.bold} font-bold`}>{row.count} kişi</span>
                    </button>
                    {open && hasNames && (
                      <div className="px-3 pb-3 -mt-1 flex flex-wrap gap-1.5" data-testid={`summary-names-${row.key}`}>
                        {row.names.map((nm, i) => (
                          <span key={i} className="px-2 py-0.5 bg-white/70 rounded-full text-xs font-medium text-gray-700 border border-white">
                            {nm}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {todaySummary?.not_recorded > 0 && (
                <div className="bg-amber-50 rounded-lg border border-amber-200" data-testid="summary-row-not_recorded">
                  <button
                    type="button"
                    onClick={() => (todaySummary?.not_recorded_names?.length > 0) && toggleRow('not_recorded')}
                    disabled={!(todaySummary?.not_recorded_names?.length > 0)}
                    className={`w-full flex items-center justify-between p-3 ${todaySummary?.not_recorded_names?.length > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                    data-testid="summary-toggle-not_recorded"
                  >
                    <span className="text-amber-700 font-medium flex items-center gap-1.5">
                      Kaydedilmemiş
                      {todaySummary?.not_recorded_names?.length > 0 && (
                        <ChevronDown className={`h-4 w-4 transition-transform ${expandedRow === 'not_recorded' ? 'rotate-180' : ''}`} />
                      )}
                    </span>
                    <span className="text-amber-800 font-bold">{todaySummary.not_recorded} kişi</span>
                  </button>
                  {expandedRow === 'not_recorded' && todaySummary?.not_recorded_names?.length > 0 && (
                    <div className="px-3 pb-3 -mt-1 flex flex-wrap gap-1.5" data-testid="summary-names-not_recorded">
                      {todaySummary.not_recorded_names.map((nm, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/70 rounded-full text-xs font-medium text-gray-700 border border-white">
                          {nm}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Dikkat Edilmesi Gerekenler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.length > 0 ? (
                alerts.map((alert, index) => {
                  const Icon = alert.icon;
                  return (
                    <div key={index} className={`p-3 rounded-lg border ${getAlertStyle(alert.type)}`}>
                      <div className="flex items-start gap-2">
                        <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">{alert.title}</p>
                          <p className="text-sm opacity-80">{alert.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Her Şey Yolunda</p>
                      <p className="text-sm text-green-600">Bugün için dikkat edilmesi gereken bir durum yok</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BranchDashboard;
